import {
  CreateWarehouseSaleDto,
  StockWarningDto,
  WarehouseQuantityMeasure,
  WarehouseUnit,
  WarehouseVatRate,
} from 'woofwoof-api';
import {
  isNotFoundError,
  warehouseErrorCode,
  warehouseErrorMessage,
} from './errors';
import {
  WAREHOUSE_MAX_MONEY_MINOR,
  formatMinor,
  milliValueMinor,
  minorToInput,
  netFromGrossMinor,
  parseMoneyToMinor,
} from './money';
import { shouldKeepOperationKey } from './operationKeyOutcome';
import { parseQuantityInput, parseQuantityMilli, toMilli } from './quantity';

// Phase 1 helpers, re-exported under the names the Sale UI uses.
export { netFromGrossMinor, parseQuantityInput };
export const parsePriceInput = parseMoneyToMinor;

export type SaleMeasure = WarehouseQuantityMeasure;
export type SaleVatRate = WarehouseVatRate;
export type CreateSalePayload = CreateWarehouseSaleDto;
export type SaleStockWarning = Pick<StockWarningDto, 'productName'>;

const SALE_VAT_RATES: readonly SaleVatRate[] = Object.values(WarehouseVatRate);
const DEFAULT_VAT_RATE: SaleVatRate = '8';
/** Backend `SALE_MAX_LINES`: a longer Sale is refused with a 400. */
export const SALE_MAX_LINES = 100;
const NO_VALUE = '-';

export interface WarehouseProductLike {
  id: string;
  name: string;
  isRx?: boolean;
  unitsPerPackage?: number;
  packagePriceGrossMinor?: number;
  unitPriceGrossMinor?: number;
  vatRate?: string;
  onHandMilli?: number;
  unit?: WarehouseUnit;
  unitCustom?: string;
}

/** The stock on hand when the Product was picked, shown on its Sale line. */
export interface SaleLineStock {
  onHandMilli: number;
  unit: WarehouseUnit;
  unitCustom?: string;
}

export interface SaleProductOption {
  id: string;
  name: string;
  isRx: boolean;
  unitsPerPackage: number;
  packagePriceGrossMinor: number;
  unitPriceGrossMinor: number;
  vatRate: SaleVatRate;
  stock?: SaleLineStock;
}

export interface SaleLineDraft {
  key: string;
  productId: string;
  productName: string;
  vatRate: SaleVatRate;
  unitsPerPackage: number;
  packagePriceGrossMinor: number;
  unitPriceGrossMinor: number;
  quantity: string;
  measure: SaleMeasure;
  price: string;
  priceEdited: boolean;
  stock?: SaleLineStock;
}

/** Grosze; `null` when a value would leave the safe-integer range (shown as "—"). */
export interface SaleTotals {
  netMinor: number | null;
  vatMinor: number | null;
  grossMinor: number | null;
}

export interface SaleTotalsItem {
  grossMinor: number;
  vatRate: SaleVatRate;
}

export interface SaleDocumentLineLike {
  inputQuantity: number;
  unitPriceGrossMinor?: number;
  vatRate: SaleVatRate;
}

export interface SaleLinesValidation {
  empty: boolean;
  invalidQuantityKeys: string[];
  invalidPriceKeys: string[];
}

export interface BuildCreateSalePayloadInput {
  lines: SaleLineDraft[];
  buyerName: string;
  note: string;
  visitId?: string;
  /** The entry point's operation key series: kept across every ambiguous outcome (R2-4). */
  operationKey: string;
}

const UNSAFE_TOTALS: SaleTotals = {
  netMinor: null,
  vatMinor: null,
  grossMinor: null,
};

function isSaleVatRate(value: string | undefined): value is SaleVatRate {
  return SALE_VAT_RATES.some((rate) => rate === value);
}

export function toSaleVatRate(value: string | undefined): SaleVatRate {
  return isSaleVatRate(value) ? value : DEFAULT_VAT_RATE;
}

export function toSaleProductOption(
  product: WarehouseProductLike,
): SaleProductOption {
  return {
    id: product.id,
    name: product.name,
    isRx: product.isRx === true,
    unitsPerPackage: product.unitsPerPackage ?? 1,
    packagePriceGrossMinor: product.packagePriceGrossMinor ?? 0,
    unitPriceGrossMinor: product.unitPriceGrossMinor ?? 0,
    vatRate: toSaleVatRate(product.vatRate),
    ...(product.onHandMilli !== undefined &&
      product.unit && {
        stock: {
          onHandMilli: product.onHandMilli,
          unit: product.unit,
          unitCustom: product.unitCustom,
        },
      }),
  };
}

export function isSellableProduct(
  product: Pick<SaleProductOption, 'isRx'>,
): boolean {
  return !product.isRx;
}

export function sortSaleOptions<
  T extends Pick<SaleProductOption, 'isRx' | 'name'>,
>(options: T[]): T[] {
  return [...options].sort((a, b) => {
    if (isSellableProduct(a) !== isSellableProduct(b)) {
      return isSellableProduct(a) ? -1 : 1;
    }
    return a.name.localeCompare(b.name, 'pl');
  });
}

export function defaultSalePriceMinor(
  product: Pick<
    SaleProductOption,
    'packagePriceGrossMinor' | 'unitPriceGrossMinor'
  >,
  measure: SaleMeasure,
): number {
  return measure === 'PACKAGE'
    ? product.packagePriceGrossMinor
    : product.unitPriceGrossMinor;
}

/** Prefilled price text with the Polish decimal comma ("49,99"). */
export function minorToPriceInput(minor: number): string {
  return minorToInput(minor);
}

export function createSaleLine(
  product: SaleProductOption,
  key: string,
): SaleLineDraft {
  const measure: SaleMeasure = 'PACKAGE';
  return {
    key,
    productId: product.id,
    productName: product.name,
    vatRate: product.vatRate,
    unitsPerPackage: product.unitsPerPackage,
    packagePriceGrossMinor: product.packagePriceGrossMinor,
    unitPriceGrossMinor: product.unitPriceGrossMinor,
    quantity: '1',
    measure,
    price: minorToPriceInput(defaultSalePriceMinor(product, measure)),
    priceEdited: false,
    ...(product.stock && { stock: product.stock }),
  };
}

export function changeSaleLineMeasure(
  line: SaleLineDraft,
  measure: SaleMeasure,
): SaleLineDraft {
  return {
    ...line,
    measure,
    price: line.priceEdited
      ? line.price
      : minorToPriceInput(defaultSalePriceMinor(line, measure)),
  };
}

export function changeSaleLinePrice(
  line: SaleLineDraft,
  price: string,
): SaleLineDraft {
  return { ...line, price, priceEdited: true };
}

export function changeSaleLineQuantity(
  line: SaleLineDraft,
  quantity: string,
): SaleLineDraft {
  return { ...line, quantity };
}

/** The typed price in grosze, within the backend maximum, or null. */
function linePriceMinor(price: string): number | null {
  const minor = parsePriceInput(price);
  return minor !== null && minor <= WAREHOUSE_MAX_MONEY_MINOR ? minor : null;
}

const hasValidQuantity = (line: Pick<SaleLineDraft, 'quantity'>): boolean =>
  parseQuantityInput(line.quantity) !== null;

const hasValidPrice = (line: Pick<SaleLineDraft, 'price'>): boolean =>
  linePriceMinor(line.price) !== null;

/** Mirror of the backend `lineValueMinor`: thousandths × grosze, half up. */
export function saleLineGrossMinor(
  line: Pick<SaleLineDraft, 'quantity' | 'price'>,
): number | null {
  const quantityMilli = parseQuantityMilli(line.quantity);
  const priceMinor = linePriceMinor(line.price);
  if (quantityMilli === null || priceMinor === null) {
    return null;
  }
  return milliValueMinor(quantityMilli, priceMinor);
}

/** Net per line (half up, like the backend), then summed; VAT is the rest. */
export function computeSaleTotals(items: SaleTotalsItem[]): SaleTotals {
  let netMinor = 0;
  let grossMinor = 0;
  for (const item of items) {
    const lineNetMinor = netFromGrossMinor(item.grossMinor, item.vatRate);
    if (lineNetMinor === null) {
      return UNSAFE_TOTALS;
    }
    netMinor += lineNetMinor;
    grossMinor += item.grossMinor;
  }
  if (!Number.isSafeInteger(netMinor) || !Number.isSafeInteger(grossMinor)) {
    return UNSAFE_TOTALS;
  }
  return { netMinor, vatMinor: grossMinor - netMinor, grossMinor };
}

/**
 * Unparseable lines are flagged at their row and left out; a valid line whose
 * value is unsafe leaves no total at all, never a partial sum (the backend
 * refuses that Sale with INVALID_QUANTITY).
 */
export function saleDraftTotals(lines: SaleLineDraft[]): SaleTotals {
  const items: SaleTotalsItem[] = [];
  for (const line of lines) {
    const grossMinor = saleLineGrossMinor(line);
    if (grossMinor !== null) {
      items.push({ grossMinor, vatRate: line.vatRate });
    } else if (hasValidQuantity(line) && hasValidPrice(line)) {
      return UNSAFE_TOTALS;
    }
  }
  return computeSaleTotals(items);
}

/** A saved `WZ` line, valued like the backend `lineValueMinor`. */
export function saleDocumentLineGrossMinor(
  line: SaleDocumentLineLike,
): number | null {
  if (line.unitPriceGrossMinor === undefined) {
    return null;
  }
  return milliValueMinor(toMilli(line.inputQuantity), line.unitPriceGrossMinor);
}

export function saleDocumentTotals(lines: SaleDocumentLineLike[]): SaleTotals {
  const items: SaleTotalsItem[] = [];
  for (const line of lines) {
    const grossMinor = saleDocumentLineGrossMinor(line);
    if (grossMinor === null) {
      return UNSAFE_TOTALS;
    }
    items.push({ grossMinor, vatRate: line.vatRate });
  }
  return computeSaleTotals(items);
}

export function validateSaleLines(lines: SaleLineDraft[]): SaleLinesValidation {
  return {
    empty: lines.length === 0,
    invalidQuantityKeys: lines
      .filter((line) => !hasValidQuantity(line))
      .map((line) => line.key),
    invalidPriceKeys: lines
      .filter((line) => !hasValidPrice(line))
      .map((line) => line.key),
  };
}

export function isSaleLinesValid(validation: SaleLinesValidation): boolean {
  return (
    !validation.empty &&
    validation.invalidQuantityKeys.length === 0 &&
    validation.invalidPriceKeys.length === 0
  );
}

function requireParsed(value: number | null): number {
  if (value === null) {
    throw new Error('buildCreateSalePayload called with unvalidated lines');
  }
  return value;
}

export function buildCreateSalePayload(
  input: BuildCreateSalePayloadInput,
): CreateSalePayload {
  const buyerName = input.buyerName.trim();
  const note = input.note.trim();
  return {
    operationKey: input.operationKey,
    lines: input.lines.map((line) => ({
      productId: line.productId,
      quantity: requireParsed(parseQuantityInput(line.quantity)),
      measure: line.measure,
      unitPriceGrossMinor: requireParsed(linePriceMinor(line.price)),
    })),
    ...(buyerName ? { buyerName } : {}),
    ...(note ? { note } : {}),
    ...(input.visitId ? { visitId: input.visitId } : {}),
  };
}

/** "12 345,67 zł"; a value that could not be computed reads "—". */
export function formatMinorPln(minor: number | null): string {
  return minor === null ? NO_VALUE : formatMinor(minor);
}

export function stockWarningProductNames(warnings: SaleStockWarning[]): string {
  return Array.from(
    new Set(warnings.map((warning) => warning.productName)),
  ).join(', ');
}

/**
 * The Polish text of a failed Sale:
 * - a domain code reads as its own message;
 * - a 404 (a deleted or foreign visit, an unknown Product) reads as in every
 *   Phase 1/2 form;
 * - an outcome that keeps the operation key (no answer, 408, 429, 5xx) may
 *   hide a committed Sale, so it reads neutrally next to the form's
 *   UnconfirmedSaveBanner, like every Phase 1/2 form;
 * - any other refusal reads as the Sale's own message, never a validator's text.
 */
export function saleErrorMessage(
  error: unknown,
  translate: (key: string) => string,
): string {
  if (warehouseErrorCode(error) || isNotFoundError(error)) {
    return warehouseErrorMessage(error, translate);
  }
  if (shouldKeepOperationKey(error)) {
    return translate('warehouse.common.genericError');
  }
  return translate('warehouse.sales.createError');
}
